import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import LandingHeader from '../components/organisms/LandingHeader';
import Button from '../components/atoms/Button';
import Icon from '../components/atoms/Icon';
import Badge from '../components/atoms/Badge';
import SessionManager from '../utils/sessionManager';
import { API_BASE_URL, FALLBACK_API_URL } from '../config';

const PlacementResultsPage = () => {
  const navigate = useNavigate();
  const { assessmentId } = useParams();
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [assessment, setAssessment] = useState(null);
  const [rounds, setRounds] = useState([]);
  const [certificate, setCertificate] = useState(null);
  const [isPolling, setIsPolling] = useState(false);
  const pollIntervalRef = useRef(null);
  const pollCountRef = useRef(0);

  const session = SessionManager.getCurrentSession();
  const apiUrl = API_BASE_URL || FALLBACK_API_URL;

  const loadResults = useCallback(async (isBackgroundPoll = false) => {
    try {
      if (!isBackgroundPoll) setLoading(true);
      const response = await fetch(
        `${apiUrl}/placement-training/assessments/${assessmentId}`,
        {
          headers: {
            'Authorization': `Bearer ${session?.access_token}`
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        setAssessment(data.assessment);
        setRounds(data.rounds || []);

        if (data.assessment.certificate_issued) {
          loadCertificate();
        }

        // Check if we should keep polling
        const hasAllScores = checkAllScoresReady(data.assessment, data.rounds || []);
        return hasAllScores;
      }
      return true; // Stop polling on error
    } catch (error) {
      console.error('Error loading results:', error);
      return true; // Stop polling on error
    } finally {
      if (!isBackgroundPoll) setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiUrl, assessmentId, session?.access_token]);

  // Check if all scores are populated
  const checkAllScoresReady = (assessmentData, roundsData) => {
    if (!assessmentData) return false;

    // Check if any completed round still has 0/null score
    const pendingRounds = roundsData.filter(
      r => r.status === 'completed' && (!r.percentage || r.percentage === 0)
    );
    if (pendingRounds.length > 0) return false;

    // Check if overall score is still 0
    if (assessmentData.status === 'completed' && (!assessmentData.overall_score || assessmentData.overall_score === 0)) {
      return false;
    }

    return true;
  };

  useEffect(() => {
    if (!session) {
      navigate('/login');
      return;
    }

    const init = async () => {
      const allReady = await loadResults(false);

      // Start polling if scores aren't ready yet
      if (!allReady) {
        setIsPolling(true);
        pollCountRef.current = 0;
        pollIntervalRef.current = setInterval(async () => {
          pollCountRef.current += 1;
          const ready = await loadResults(true);

          // Stop after scores are ready or after 12 polls (60 seconds)
          if (ready || pollCountRef.current >= 12) {
            clearInterval(pollIntervalRef.current);
            pollIntervalRef.current = null;
            setIsPolling(false);
          }
        }, 5000);
      }
    };

    init();

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadCertificate = async () => {
    try {
      const response = await fetch(
        `${apiUrl}/placement-training/certificates/${assessmentId}`,
        {
          headers: {
            'Authorization': `Bearer ${session.access_token}`
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        setCertificate(data.certificate);
      }
    } catch (error) {
      console.error('Error loading certificate:', error);
    }
  };

  const handleGenerateCertificate = async () => {
    try {
      setGenerating(true);
      const response = await fetch(
        `${apiUrl}/placement-training/assessments/${assessmentId}/generate-certificate`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`
          }
        }
      );

      if (response.ok) {
        await loadCertificate();
        alert('Certificate generated successfully!');
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to generate certificate');
      }
    } catch (error) {
      console.error('Error generating certificate:', error);
      alert('An error occurred. Please try again.');
    } finally {
      setGenerating(false);
    }
  };

  const getRoundIcon = (roundType) => {
    switch (roundType) {
      case 'aptitude': return 'quiz';
      case 'coding': return 'code';
      case 'advanced_coding': return 'code';
      case 'tech_interview': return 'engineering';
      case 'hr_interview': return 'people';
      default: return 'circle';
    }
  };

  const getGrade = (score) => {
    if (score >= 90) return { grade: 'A+', color: 'text-green-600' };
    if (score >= 80) return { grade: 'A', color: 'text-green-600' };
    if (score >= 70) return { grade: 'B+', color: 'text-blue-600' };
    if (score >= 60) return { grade: 'B', color: 'text-blue-600' };
    if (score >= 50) return { grade: 'C', color: 'text-yellow-600' };
    return { grade: 'F', color: 'text-red-600' };
  };

  const getScoreColor = (score) => {
    if (score >= 70) return 'text-green-600';
    if (score >= 50) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getScoreBgColor = (score) => {
    if (score >= 70) return 'bg-green-50 border-green-200';
    if (score >= 50) return 'bg-yellow-50 border-yellow-200';
    return 'bg-red-50 border-red-200';
  };

  // Build display rounds - combine tech+HR if both exist (combined interview round)
  const getDisplayRounds = () => {
    const hasCombinedInterview = assessment?.has_combined_interview;
    const techRound = rounds.find(r => r.round_type === 'tech_interview');
    const hrRound = rounds.find(r => r.round_type === 'hr_interview');

    if (hasCombinedInterview && techRound && hrRound) {
      // Combine tech + HR into one display entry
      const combinedScore = (() => {
        const scores = [];
        if (techRound.percentage && techRound.percentage > 0) scores.push(techRound.percentage);
        if (hrRound.percentage && hrRound.percentage > 0) scores.push(hrRound.percentage);
        return scores.length > 0 ? scores.reduce((a, b) => a + b, 0) / scores.length : 0;
      })();

      const combinedStatus = (techRound.status === 'completed' && hrRound.status === 'completed')
        ? 'completed'
        : (techRound.status === 'in_progress' || hrRound.status === 'in_progress')
          ? 'in_progress'
          : 'not_started';

      return rounds
        .filter(r => r.round_type !== 'tech_interview' && r.round_type !== 'hr_interview')
        .concat([{
          id: `${techRound.id}-${hrRound.id}`,
          round_name: 'Technical + HR Interview',
          round_type: 'tech_hr_combined',
          round_number: techRound.round_number,
          status: combinedStatus,
          score: combinedScore,
          percentage: combinedScore,
          questions_attempted: (techRound.questions_attempted || 0) + (hrRound.questions_attempted || 0),
          questions_correct: (techRound.questions_correct || 0) + (hrRound.questions_correct || 0),
          time_taken_seconds: (techRound.time_taken_seconds || 0) + (hrRound.time_taken_seconds || 0),
        }])
        .sort((a, b) => (a.round_number || 0) - (b.round_number || 0));
    }

    return rounds;
  };

  // Build score breakdown items dynamically
  const getScoreBreakdown = () => {
    const hasCombinedInterview = assessment?.has_combined_interview;
    const items = [];

    if (assessment.aptitude_score !== null && assessment.aptitude_score !== undefined) {
      items.push({ label: 'Aptitude', score: assessment.aptitude_score, icon: 'quiz' });
    }
    if (assessment.coding_score !== null && assessment.coding_score !== undefined) {
      items.push({ label: 'Coding', score: assessment.coding_score, icon: 'code' });
    }
    if (assessment.advanced_coding_score !== null && assessment.advanced_coding_score !== undefined) {
      items.push({ label: 'Advanced Coding', score: assessment.advanced_coding_score, icon: 'code' });
    }

    if (hasCombinedInterview) {
      // Combine tech + HR into single entry
      const techScore = assessment.tech_interview_score || 0;
      const hrScore = assessment.hr_interview_score || 0;
      const scores = [];
      if (techScore > 0) scores.push(techScore);
      if (hrScore > 0) scores.push(hrScore);
      const combinedScore = scores.length > 0 ? scores.reduce((a, b) => a + b, 0) / scores.length : 0;

      items.push({ label: 'Technical + HR', score: combinedScore, icon: 'engineering' });
    } else {
      if (assessment.tech_interview_score !== null && assessment.tech_interview_score !== undefined) {
        items.push({ label: 'Technical', score: assessment.tech_interview_score, icon: 'engineering' });
      }
      if (assessment.hr_interview_score !== null && assessment.hr_interview_score !== undefined) {
        items.push({ label: 'HR', score: assessment.hr_interview_score, icon: 'people' });
      }
    }

    // If no items found, fall back to showing all 4 categories
    if (items.length === 0) {
      return [
        { label: 'Aptitude', score: assessment.aptitude_score, icon: 'quiz' },
        { label: 'Coding', score: assessment.coding_score, icon: 'code' },
        { label: 'Technical', score: assessment.tech_interview_score, icon: 'engineering' },
        { label: 'HR', score: assessment.hr_interview_score, icon: 'people' }
      ];
    }

    return items;
  };

  if (loading) {
    return (
      <div className="relative flex h-screen w-full flex-col overflow-x-hidden">
        <LandingHeader />
        <main className="layout-container flex h-full grow flex-col items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-text-light-secondary dark:text-text-dark-secondary">
              Loading your results...
            </p>
          </div>
        </main>
      </div>
    );
  }

  if (!assessment) {
    return (
      <div className="relative flex h-screen w-full flex-col overflow-x-hidden">
        <LandingHeader />
        <main className="layout-container flex h-full grow flex-col items-center justify-center">
          <div className="rounded-xl border border-red-200 bg-red-50 p-6 text-red-700 max-w-md">
            Assessment not found
          </div>
          <Button className="mt-4" onClick={() => navigate('/placement-training')}>
            Back to Training
          </Button>
        </main>
      </div>
    );
  }

  const displayRounds = getDisplayRounds();
  const scoreBreakdown = getScoreBreakdown();
  const hasPendingRounds = assessment.has_pending_rounds ||
    rounds.some(r => r.status === 'not_started' || r.status === 'in_progress');

  // Map round_type to the route path for continuing
  const getRoundPath = (roundType) => {
    switch (roundType) {
      case 'aptitude': return 'aptitude';
      case 'coding': return 'coding';
      case 'advanced_coding': return 'coding?type=advanced_coding';
      case 'tech_interview': return 'tech-interview';
      case 'hr_interview': return 'hr-interview';
      case 'tech_hr_combined': return 'tech-interview';
      default: return null;
    }
  };

  return (
    <div className="relative flex h-auto min-h-screen w-full flex-col overflow-x-hidden">
      <LandingHeader />

      <main className="layout-container flex h-full grow flex-col">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="layout-content-container flex flex-col w-full flex-1">
            {/* Breadcrumb */}
            <div className="flex flex-wrap gap-2 pb-6">
              <button
                type="button"
                onClick={() => navigate('/placement-training')}
                className="text-text-light-secondary dark:text-text-dark-secondary text-sm font-medium leading-normal hover:text-primary"
              >
                Placement Training
              </button>
              <span className="text-text-light-secondary dark:text-text-dark-secondary text-sm font-medium leading-normal">/</span>
              <span className="text-text-light-primary dark:text-text-dark-primary text-sm font-medium leading-normal">
                Assessment Results
              </span>
            </div>

            {/* Page Title */}
            <div className="flex flex-col gap-2 pb-8">
              <h1 className="text-text-light-primary dark:text-text-dark-primary text-3xl md:text-4xl font-black leading-tight tracking-[-0.033em]">
                Assessment Results
              </h1>
              <p className="text-text-light-secondary dark:text-text-dark-secondary text-base">
                {assessment.company_name} - {assessment.position_title}
              </p>
            </div>

            {/* Evaluation Pending Banner */}
            {isPolling && (
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6 flex items-center gap-3">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600 flex-shrink-0"></div>
                <div>
                  <div className="font-medium text-blue-900">Evaluating your performance...</div>
                  <div className="text-sm text-blue-700">
                    AI is analyzing your responses. Scores will update automatically.
                  </div>
                </div>
              </div>
            )}

            {/* Overall Score Card */}
            <div className={`rounded-xl border p-6 mb-6 ${hasPendingRounds ? 'bg-blue-50 border-blue-200' : getScoreBgColor(assessment.overall_score || 0)}`}>
              <div className="flex flex-col md:flex-row items-center justify-between gap-6">
                <div className="flex items-center gap-4">
                  <div className={`w-16 h-16 rounded-full flex items-center justify-center ${
                    hasPendingRounds ? 'bg-blue-100' : assessment.passed ? 'bg-green-100' : 'bg-red-100'
                  }`}>
                    <Icon
                      name={hasPendingRounds ? 'pending' : assessment.passed ? 'check_circle' : 'cancel'}
                      size={40}
                      className={hasPendingRounds ? 'text-blue-600' : assessment.passed ? 'text-green-600' : 'text-red-600'}
                    />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-text-light-primary dark:text-text-dark-primary mb-1">
                      {hasPendingRounds
                        ? 'Assessment In Progress'
                        : assessment.passed ? 'Congratulations!' : 'Assessment Complete'}
                    </h2>
                    <p className="text-sm text-text-light-secondary dark:text-text-dark-secondary">
                      {hasPendingRounds
                        ? 'You have pending rounds to complete. Finish all rounds to see your final result.'
                        : assessment.passed
                          ? 'You have successfully passed the assessment'
                          : `Minimum ${assessment.passing_threshold}% required to pass`
                      }
                    </p>
                  </div>
                </div>

                <div className="text-center">
                  <div className={`text-5xl font-black mb-2 ${hasPendingRounds ? 'text-blue-600' : getScoreColor(assessment.overall_score || 0)}`}>
                    {assessment.overall_score?.toFixed(1) || 0}%
                  </div>
                  {!hasPendingRounds && (
                    <Badge variant={assessment.passed ? 'success' : 'secondary'}>
                      Grade: {getGrade(assessment.overall_score || 0).grade}
                    </Badge>
                  )}
                  {hasPendingRounds && (
                    <span className="text-xs text-blue-600 font-medium">Partial Score</span>
                  )}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-wrap gap-3 mt-6">
                {!hasPendingRounds && assessment.passed && certificate && (
                  <Button
                    size="lg"
                    onClick={() => navigate(`/placement-training/certificate/${certificate.id}`)}
                  >
                    <Icon name="workspace_premium" size={20} className="mr-2" />
                    View Certificate
                  </Button>
                )}
                {!hasPendingRounds && assessment.passed && !certificate && (
                  <Button
                    size="lg"
                    onClick={handleGenerateCertificate}
                    disabled={generating}
                  >
                    <Icon name="workspace_premium" size={20} className="mr-2" />
                    {generating ? 'Generating...' : 'Generate Certificate'}
                  </Button>
                )}
                {!hasPendingRounds && !assessment.passed && (
                  <Button
                    size="lg"
                    onClick={() => navigate(`/placement-training/position/${assessment.position_id}`)}
                  >
                    <Icon name="refresh" size={20} className="mr-2" />
                    Retry Assessment
                  </Button>
                )}
                <Button
                  size="lg"
                  variant="outline"
                  onClick={() => navigate('/placement-training')}
                >
                  Back to Training
                </Button>
              </div>
            </div>

            {/* Two Column Layout */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Left Column */}
              <div className="flex flex-col gap-6">
                {/* Round-wise Performance */}
                <div className="bg-card-light dark:bg-card-dark rounded-xl shadow-soft p-6">
                  <h3 className="text-lg font-bold text-text-light-primary dark:text-text-dark-primary mb-4 flex items-center">
                    <Icon name="assessment" size={24} className="mr-2 text-primary" />
                    Round-wise Performance
                  </h3>
                  <div className="space-y-3">
                    {displayRounds.map((round) => {
                      const roundGrade = getGrade(round.percentage || 0);
                      const isEvaluating = round.status === 'completed' && (!round.percentage || round.percentage === 0);
                      const isRoundPending = round.status === 'not_started' || round.status === 'in_progress';
                      const roundPath = getRoundPath(round.round_type);
                      return (
                        <div
                          key={round.id}
                          className={`flex items-center justify-between p-4 border rounded-lg ${
                            isRoundPending ? 'border-orange-300 bg-orange-50' : 'border-border-light dark:border-border-dark'
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                              isRoundPending ? 'bg-orange-100' : 'bg-primary/10'
                            }`}>
                              <Icon name={getRoundIcon(round.round_type)} size={20} className={isRoundPending ? 'text-orange-600' : 'text-primary'} />
                            </div>
                            <div>
                              <div className="font-semibold text-sm text-text-light-primary dark:text-text-dark-primary">
                                {round.round_name}
                              </div>
                              <div className="text-xs text-text-light-secondary dark:text-text-dark-secondary">
                                {isRoundPending ? (
                                  <span className="text-orange-600 font-medium">
                                    {round.status === 'in_progress' ? 'In Progress - Resume to continue' : 'Not started yet'}
                                  </span>
                                ) : round.round_type === 'tech_hr_combined' ? (
                                  <span>Combined Technical + HR Interview</span>
                                ) : (
                                  <>
                                    {round.questions_correct || 0} / {round.questions_attempted || 0} correct
                                    {round.time_taken_seconds > 0 && (
                                      <span className="ml-2">
                                        &bull; {Math.floor(round.time_taken_seconds / 60)}m
                                      </span>
                                    )}
                                  </>
                                )}
                              </div>
                            </div>
                          </div>

                          <div className="text-right">
                            {isRoundPending ? (
                              <button
                                onClick={() => {
                                  if (roundPath) {
                                    navigate(`/placement-training/assessment/${assessmentId}/${roundPath}`);
                                  }
                                }}
                                className="inline-flex items-center gap-1.5 px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white text-sm font-semibold rounded-lg transition-colors"
                              >
                                <Icon name={round.status === 'in_progress' ? 'play_arrow' : 'arrow_forward'} size={18} />
                                {round.status === 'in_progress' ? 'Continue' : 'Start'}
                              </button>
                            ) : isEvaluating ? (
                              <div className="flex items-center gap-2">
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                                <span className="text-sm text-blue-600">Evaluating...</span>
                              </div>
                            ) : (
                              <>
                                <div className={`text-xl font-bold ${getScoreColor(round.percentage || 0)}`}>
                                  {round.percentage?.toFixed(1) || 0}%
                                </div>
                                <div className={`text-xs font-medium ${roundGrade.color}`}>
                                  {roundGrade.grade}
                                </div>
                              </>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Motivational Message */}
                {hasPendingRounds ? (
                  <div className="bg-card-light dark:bg-card-dark rounded-xl shadow-soft p-6">
                    <h3 className="text-lg font-bold text-text-light-primary dark:text-text-dark-primary mb-4 flex items-center">
                      <Icon name="schedule" size={24} className="mr-2 text-orange-500" />
                      Rounds Remaining
                    </h3>
                    <p className="text-sm text-text-light-secondary dark:text-text-dark-secondary leading-relaxed mb-3">
                      You have pending rounds to complete. Your final score will be the average of all rounds.
                      Complete all rounds to see if you pass (minimum {assessment.passing_threshold || 50}% overall average required).
                    </p>
                    <p className="text-sm text-orange-600 font-medium">
                      Click the "Start" or "Continue" button on pending rounds above to proceed.
                    </p>
                  </div>
                ) : assessment.passed ? (
                  <div className="bg-card-light dark:bg-card-dark rounded-xl shadow-soft p-6">
                    <h3 className="text-lg font-bold text-text-light-primary dark:text-text-dark-primary mb-4 flex items-center">
                      <Icon name="emoji_events" size={24} className="mr-2 text-yellow-500" />
                      Excellent Performance!
                    </h3>
                    <p className="text-sm text-text-light-secondary dark:text-text-dark-secondary leading-relaxed">
                      You've demonstrated strong skills across all assessment rounds. Your performance shows you're well-prepared
                      for real-world interviews. Keep up the great work and continue practicing to maintain your edge!
                    </p>
                  </div>
                ) : (
                  <div className="bg-card-light dark:bg-card-dark rounded-xl shadow-soft p-6">
                    <h3 className="text-lg font-bold text-text-light-primary dark:text-text-dark-primary mb-4 flex items-center">
                      <Icon name="lightbulb" size={24} className="mr-2 text-yellow-500" />
                      Keep Practicing!
                    </h3>
                    <p className="text-sm text-text-light-secondary dark:text-text-dark-secondary leading-relaxed">
                      {assessment.overall_score > 0 ? (
                        <>
                          You scored {assessment.overall_score?.toFixed(1)}%, just {(assessment.passing_threshold - (assessment.overall_score || 0)).toFixed(1)}%
                          away from passing. Review the areas where you can improve and try again. Every attempt is a learning opportunity!
                        </>
                      ) : (
                        <>
                          Your scores are being calculated. Please wait a moment for the AI evaluation to complete.
                          The page will update automatically.
                        </>
                      )}
                    </p>
                  </div>
                )}
              </div>

              {/* Right Column */}
              <div className="flex flex-col gap-6">
                {/* Score Breakdown */}
                <div className="bg-card-light dark:bg-card-dark rounded-xl shadow-soft p-6">
                  <h3 className="text-lg font-bold text-text-light-primary dark:text-text-dark-primary mb-4">
                    Score Breakdown
                  </h3>
                  <div className={`grid gap-4 ${scoreBreakdown.length <= 2 ? 'grid-cols-2' : scoreBreakdown.length === 3 ? 'grid-cols-3' : 'grid-cols-2'}`}>
                    {scoreBreakdown.map((item, idx) => (
                      <div key={idx} className="border border-border-light dark:border-border-dark rounded-lg p-4">
                        <div className="flex items-center gap-2 mb-2">
                          <Icon name={item.icon} size={18} className="text-primary" />
                          <span className="text-xs font-medium text-text-light-secondary dark:text-text-dark-secondary">
                            {item.label}
                          </span>
                        </div>
                        <div className={`text-2xl font-bold ${getScoreColor(item.score || 0)}`}>
                          {item.score?.toFixed(1) || 0}%
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Certificate Section */}
                {!hasPendingRounds && assessment.passed && (
                  <div className="bg-card-light dark:bg-card-dark rounded-xl shadow-soft p-6">
                    <h3 className="text-lg font-bold text-text-light-primary dark:text-text-dark-primary mb-4 flex items-center">
                      <Icon name="workspace_premium" size={24} className="mr-2 text-yellow-500" />
                      {certificate ? 'Certificate Ready' : 'Earn Your Certificate'}
                    </h3>
                    <p className="text-sm text-text-light-secondary dark:text-text-dark-secondary mb-4">
                      {certificate
                        ? 'Your completion certificate is ready to view and download. Share it on your professional profiles!'
                        : 'Generate your official completion certificate to showcase your achievement.'
                      }
                    </p>
                    {certificate ? (
                      <Button
                        variant="outline"
                        onClick={() => navigate(`/placement-training/certificate/${certificate.id}`)}
                        className="w-full"
                      >
                        <Icon name="visibility" size={20} className="mr-2" />
                        View Certificate
                      </Button>
                    ) : (
                      <Button
                        variant="outline"
                        onClick={handleGenerateCertificate}
                        disabled={generating}
                        className="w-full"
                      >
                        {generating ? (
                          <>
                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary mr-2"></div>
                            Generating...
                          </>
                        ) : (
                          <>
                            <Icon name="add_circle" size={20} className="mr-2" />
                            Generate Certificate
                          </>
                        )}
                      </Button>
                    )}
                  </div>
                )}

                {/* Next Steps */}
                <div className="bg-card-light dark:bg-card-dark rounded-xl shadow-soft p-6">
                  <h3 className="text-lg font-bold text-text-light-primary dark:text-text-dark-primary mb-4">
                    Next Steps
                  </h3>
                  <ul className="space-y-3">
                    {hasPendingRounds ? (
                      <>
                        <li className="flex items-start gap-2">
                          <Icon name="arrow_forward" size={18} className="text-orange-500 mt-0.5 flex-shrink-0" />
                          <span className="text-sm text-text-light-secondary dark:text-text-dark-secondary">
                            Complete all remaining rounds to get your final score
                          </span>
                        </li>
                        <li className="flex items-start gap-2">
                          <Icon name="arrow_forward" size={18} className="text-orange-500 mt-0.5 flex-shrink-0" />
                          <span className="text-sm text-text-light-secondary dark:text-text-dark-secondary">
                            Your overall score is the average of all rounds (must be {assessment.passing_threshold || 50}%+ to pass)
                          </span>
                        </li>
                        <li className="flex items-start gap-2">
                          <Icon name="arrow_forward" size={18} className="text-orange-500 mt-0.5 flex-shrink-0" />
                          <span className="text-sm text-text-light-secondary dark:text-text-dark-secondary">
                            Certificate is awarded only after passing all rounds
                          </span>
                        </li>
                      </>
                    ) : assessment.passed ? (
                      <>
                        <li className="flex items-start gap-2">
                          <Icon name="check_circle" size={18} className="text-green-600 mt-0.5 flex-shrink-0" />
                          <span className="text-sm text-text-light-secondary dark:text-text-dark-secondary">
                            Try assessments from other companies to broaden your experience
                          </span>
                        </li>
                        <li className="flex items-start gap-2">
                          <Icon name="check_circle" size={18} className="text-green-600 mt-0.5 flex-shrink-0" />
                          <span className="text-sm text-text-light-secondary dark:text-text-dark-secondary">
                            Download and share your certificate on LinkedIn
                          </span>
                        </li>
                        <li className="flex items-start gap-2">
                          <Icon name="check_circle" size={18} className="text-green-600 mt-0.5 flex-shrink-0" />
                          <span className="text-sm text-text-light-secondary dark:text-text-dark-secondary">
                            Apply for real job positions with confidence
                          </span>
                        </li>
                      </>
                    ) : (
                      <>
                        <li className="flex items-start gap-2">
                          <Icon name="school" size={18} className="text-primary mt-0.5 flex-shrink-0" />
                          <span className="text-sm text-text-light-secondary dark:text-text-dark-secondary">
                            Review the questions you found challenging
                          </span>
                        </li>
                        <li className="flex items-start gap-2">
                          <Icon name="school" size={18} className="text-primary mt-0.5 flex-shrink-0" />
                          <span className="text-sm text-text-light-secondary dark:text-text-dark-secondary">
                            Practice similar problems to strengthen weak areas
                          </span>
                        </li>
                        <li className="flex items-start gap-2">
                          <Icon name="school" size={18} className="text-primary mt-0.5 flex-shrink-0" />
                          <span className="text-sm text-text-light-secondary dark:text-text-dark-secondary">
                            Retry the assessment when you feel ready
                          </span>
                        </li>
                      </>
                    )}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default PlacementResultsPage;
